import { PL_COUNTRY_CODE, BANK_ACCOUNT_NUMBER_REGEX } from "@/constants";

// Validates NRB format (exactly 26 digits)
export function validateNRBFormat(nrb: string): boolean {
  return BANK_ACCOUNT_NUMBER_REGEX.test(nrb);
}

// Rearranges IBAN by moving first 4 characters to the end (needed for MOD-97)
const rearrangeIban = (iban: string): string =>
  iban.slice(4) + iban.slice(0, 4);

// Converts letters in IBAN to numbers (A=10, B=11, ..., Z=35); digits remain unchanged
// '55' is used to correctly map letters to IBAN values (e.g., 'A'.charCodeAt(0) - 55 = 10)
const lettersToNumbers = (ibanPart: string): string =>
  ibanPart.replace(/[A-Z]/g, (c) => (c.charCodeAt(0) - 55).toString());

// Performs MOD-97 check on NRB using BigInt for safe large-number modulo
const isValidMod97 = (nrb: string): boolean => {
  const iban = PL_COUNTRY_CODE + nrb;
  const numeric = lettersToNumbers(rearrangeIban(iban));
  return BigInt(numeric) % 97n === 1n;
};

/**
 * Validates a Polish Bank Account Number (NRB)
 * This is only a checksum validation, not a full validation of the NRB with real banks.
 * Based on the ISO/IEC 7064 standard described in:
 * https://en.wikipedia.org/wiki/International_Bank_Account_Number#Validating_the_IBAN
 * @param nrb - The NRB to validate
 * @returns True if the NRB is valid, false otherwise
 */
export const isValidNRB = (nrb: string): boolean => {
  if (!validateNRBFormat(nrb)) {
    return false;
  }
  try {
    return isValidMod97(nrb);
  } catch (err) {
    console.error("Error in isValidMod97:", err);
    return false;
  }
};
